import { useMutation, useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { GET_SALON_CONFIGURATIONS, UPDATE_SALON_CONFIGURATION } from "@/graphql/configuration";
import { useConfigurationSubscription } from "@/hooks/use-configuration-subscription";
import { useSalonStore } from "@/lib/store/salon";
import type { PersistField } from "../../../hooks/use-auto-save-field";
import { useAutoSaveField } from "../../../hooks/use-auto-save-field";
import type { BookingBehaviorFormData } from "../types";

export function useBookingBehaviorSettings() {
  const { currentSalon } = useSalonStore();
  const salonId = currentSalon?.id;

  // Real-time updates so changes from Partner/another tab show up immediately.
  useConfigurationSubscription({
    salonId: salonId ?? "",
    categories: ["booking_behavior"],
    enabled: !!salonId,
  });

  // GraphQL query for booking behavior configurations
  const {
    data: configData,
    loading: configLoading,
    error: configError,
    refetch,
  } = useQuery(GET_SALON_CONFIGURATIONS, {
    variables: {
      salonId: salonId ?? "",
      category: "booking_behavior",
    },
    skip: !salonId,
  });

  // GraphQL mutation for updating configurations
  const [updateConfiguration] = useMutation(UPDATE_SALON_CONFIGURATION);

  // Transform GraphQL data to form data
  const defaultValues: BookingBehaviorFormData = useMemo(() => {
    const configs = configData?.getSalonConfigurations?.configurations || [];
    const configMap = new Map(configs.map((c) => [c.key, c.value]));

    const bookingMode =
      (configMap.get("booking_behavior.mode") as "manual" | "auto" | "ai") || "manual";

    return {
      bookingMode,

      // Manual mode
      manualCountPendingTowardCapacity:
        configMap.get("booking_behavior.manual.count_pending_toward_capacity") === "true",

      // Auto mode
      autoArrangeWorkers: configMap.get("booking_behavior.auto.arrange_workers") !== "false",
      autoConfirmImmediately:
        configMap.get("booking_behavior.auto.confirm_immediately") !== "false",
      autoBatchConfirmInterval:
        Number(configMap.get("booking_behavior.auto.batch_confirm_interval")) || 15,

      // AI mode
      aiNaturalLanguageRules: configMap.get("booking_behavior.ai.natural_language_rules") || "",
      aiSmartGapFilling: configMap.get("booking_behavior.ai.smart_gap_filling") !== "false",
      aiServiceRecommendations:
        configMap.get("booking_behavior.ai.service_recommendations") !== "false",
    };
  }, [configData]);

  // Auto-sync with Apollo cache (initial load + subscription updates) while
  // preserving user edits in flight.
  const form = useForm<BookingBehaviorFormData>({
    values: defaultValues,
    resetOptions: { keepDirtyValues: true },
    mode: "onChange",
  });

  // Transform form data to configuration updates
  const transformToConfigurations = useCallback((formData: BookingBehaviorFormData) => {
    return (Object.keys(bookingBehaviorFields) as (keyof BookingBehaviorFormData)[]).map(
      (name) => ({
        ...bookingBehaviorFields[name],
        value: String(formData[name]),
      })
    );
  }, []);

  const persistField: PersistField<BookingBehaviorFormData> = useCallback(
    async (name, value) => {
      if (!salonId) {
        throw new Error("No salon selected");
      }
      return updateConfiguration({
        variables: {
          salonId,
          input: { ...bookingBehaviorFields[name], value: String(value) },
        },
        refetchQueries: [GET_SALON_CONFIGURATIONS],
      });
    },
    [salonId, updateConfiguration]
  );
  const { saveField, savingFields } = useAutoSaveField(form, persistField);

  // Save handler
  const save = useCallback(async () => {
    try {
      const formData = form.getValues();
      const configurations = transformToConfigurations(formData);

      await Promise.all(
        configurations.map((config) =>
          updateConfiguration({
            variables: {
              salonId: salonId ?? "",
              input: config,
            },
          })
        )
      );

      await refetch();
      form.reset(formData);
      toast.success("Booking behavior settings saved successfully");
    } catch (error) {
      console.error("Failed to save booking behavior settings:", error);
      toast.error("Failed to save settings. Please try again.");
    }
  }, [form, transformToConfigurations, updateConfiguration, refetch, salonId]);

  // Discard handler
  const discard = useCallback(() => {
    form.reset(defaultValues);
  }, [form, defaultValues]);

  return {
    form,
    loading: configLoading,
    saving: false, // TODO: Add proper saving state
    error: configError,
    save,
    discard,
    saveField,
    savingFields,
  };
}

const bookingBehaviorFields: Record<
  keyof BookingBehaviorFormData,
  { key: string; dataType: string }
> = {
  bookingMode: { key: "booking_behavior.mode", dataType: "booking_behavior_mode" },
  manualCountPendingTowardCapacity: {
    key: "booking_behavior.manual.count_pending_toward_capacity",
    dataType: "boolean",
  },
  autoArrangeWorkers: { key: "booking_behavior.auto.arrange_workers", dataType: "boolean" },
  autoConfirmImmediately: {
    key: "booking_behavior.auto.confirm_immediately",
    dataType: "boolean",
  },
  autoBatchConfirmInterval: {
    key: "booking_behavior.auto.batch_confirm_interval",
    dataType: "int",
  },
  aiNaturalLanguageRules: {
    key: "booking_behavior.ai.natural_language_rules",
    dataType: "string",
  },
  aiSmartGapFilling: { key: "booking_behavior.ai.smart_gap_filling", dataType: "boolean" },
  aiServiceRecommendations: {
    key: "booking_behavior.ai.service_recommendations",
    dataType: "boolean",
  },
};
